<template>
  <div v-if="application" class="application-editor">
    <section>
      <h1 v-if="isLb" class="page-title mb-2">Överblick över byteskedjan</h1>
      <h1 v-else class="page-title mb-2">
        Skapa byteskedjan och visa vem som ska flytta vart
      </h1>
      <ApplicationReviewedCard v-if="applicationIsSent" />
      <CollapseCard v-if="notStarted?.length > 0" is-primary class="mb-4">
        <h3 slot="title-text">Välkommen!</h3>
        <template slot="body-text">
          <p>
            Vi är glada att du har hittat ett byte och ser fram emot att hjälpa
            dig hela vägen till ditt nya boende.
          </p>
          <br />
          <p>
            Läs igenom våra villkor
            <a
              href="https://www.lagenhetsbyte.se/allmanna-villkor"
              target="_blank"
              rel="noopener noreferrer"
              >här</a
            >
          </p>
          <br />
          <h3 slot="title-text">Nu behövs följande:</h3>
          <ul>
            <li>
              <strong>Information om dig och din lägenhet</strong><br />Fyll i
              detaljer om dig och din lägenhet som krävs av din hyresvärd.<br /><br />
            </li>
            <li>
              <strong>Ladda upp dokument</strong><br />Skicka in personbevis,
              hyresavtal och andra dokument som behövs i bytesprocessen.<br /><br />
            </li>
            <li>
              <strong>Signera bytesansökan</strong><br />Skriv under med hjälp
              av BankID för att färdigställa ansökan.
            </li>
          </ul>
        </template>
      </CollapseCard>
      <CollapseCard
        v-if="!isLb"
        is-primary
        class="mb-4"
        closed-identifier="how_it_works_modal_closed"
      >
        <h3 slot="title-text">Hur fungerar det?</h3>
        <template slot="body-text">
          <h3>Direktbyte</h3>
          <p>
            Skriv för- och efternamn på lägenheterna A och B och klicka dig
            vidare till formulären.
          </p>
          <br />
          <h3>Flerpartsbyte</h3>
          <p>
            Börja med att lägga till en bytespart med hjälp av den gröna knappen
            i botten. Nu har du tre bytesparter där du ska skriva in namn innan
            du fortsätter till formulären.
          </p>
          <br />
          <h3>2 mot 1</h3>
          <p>
            Har en bytespart 2 lägenheter kan du lägga till en lägenhet genom
            att klicka på “Har 2 lägenheter” på aktuell bytespart. Fyll i namnen
            som representerar lägenheterna.
          </p>
          <br />
          <h3>Fler på ett kontrakt</h3>
          <p>
            Står det fler på kontraktet till någon bytespart lägenhet anges det
            i ett senare skede.
          </p>
        </template>
      </CollapseCard>
      <div
        v-for="(applicant, idx) in form.applicants"
        :key="idx"
        class="application-wrapper"
      >
        <div v-if="idx === 0" class="side-swap-arrow-wrapper first">
          <div class="arrow-body"></div>
          <div class="arrow-right-tip"></div>
        </div>
        <div
          v-else-if="!isAddingApplicant && idx === form.applicants.length - 1"
          class="side-swap-arrow-wrapper last"
        >
          <div class="arrow-body"></div>
        </div>
        <div v-else class="side-swap-arrow-wrapper"></div>

        <div
          class="applicant-card"
          :class="{
            'is-saving': getApplicant(idx).isSaving,
            'is-locked': !getApplicant(idx).owner
          }"
        >
          <div
            v-if="isRemovingApplicant(idx) || isTogglingApartmentCount(idx)"
            class="applicant-spinner"
          >
            <BaseLoadingSpinner />
            <p v-if="isRemovingApplicant(idx)">Tar bort...</p>
          </div>
          <div
            class="applicant-card-head"
            :class="{
              'is-blurry':
                isRemovingApplicant(idx) || isTogglingApartmentCount(idx)
            }"
          >
            <svg
              v-for="(_, i) in getApartments(idx)"
              :key="i"
              class="icon"
              width="40"
              height="41"
              viewBox="0 0 40 41"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M39.3284 17.504L21.3284 1.50399C20.9626 1.17856 20.49 0.998779 20.0004 0.998779C19.5108 0.998779 19.0382 1.17856 18.6724 1.50399L0.672403 17.504C0.275639 17.8562 0.0350397 18.3516 0.00353359 18.8812C-0.0279725 19.4108 0.152196 19.9312 0.504403 20.328C0.856611 20.7248 1.35201 20.9654 1.88161 20.9969C2.41121 21.0284 2.93164 20.8482 3.3284 20.496L4.0004 19.898V39C4.0004 39.5304 4.21112 40.0391 4.58619 40.4142C4.96126 40.7893 5.46997 41 6.0004 41H34.0004C34.5308 41 35.0395 40.7893 35.4146 40.4142C35.7897 40.0391 36.0004 39.5304 36.0004 39V19.898L36.6724 20.496C37.0692 20.8482 37.5896 21.0284 38.1192 20.9969C38.3814 20.9813 38.638 20.9142 38.8743 20.7994C39.1106 20.6846 39.322 20.5244 39.4964 20.328C39.6708 20.1315 39.8048 19.9026 39.8907 19.6544C39.9767 19.4062 40.0129 19.1434 39.9973 18.8812C39.9817 18.619 39.9146 18.3624 39.7998 18.1261C39.685 17.8898 39.5249 17.6784 39.3284 17.504ZM14.3684 37C14.7757 35.8309 15.5367 34.8175 16.5459 34.1004C17.5551 33.3834 18.7624 32.9981 20.0004 32.9981C21.2384 32.9981 22.4457 33.3834 23.4549 34.1004C24.4641 34.8175 25.2251 35.8309 25.6324 37H14.3684ZM17.0004 26C17.0004 25.4066 17.1763 24.8266 17.506 24.3333C17.8356 23.8399 18.3042 23.4554 18.8524 23.2284C19.4005 23.0013 20.0037 22.9419 20.5857 23.0576C21.1676 23.1734 21.7022 23.4591 22.1217 23.8787C22.5413 24.2982 22.827 24.8328 22.9428 25.4147C23.0585 25.9967 22.9991 26.5999 22.772 27.148C22.545 27.6962 22.1605 28.1648 21.6671 28.4944C21.1738 28.824 20.5937 29 20.0004 29C19.2051 28.9989 18.4426 28.6825 17.8803 28.1201C17.3179 27.5578 17.0015 26.7953 17.0004 26ZM32.0004 37H29.7984C29.5218 35.674 28.9787 34.418 28.2021 33.3082C27.4255 32.1984 26.4315 31.258 25.2804 30.544C26.3828 29.2862 26.9937 27.6725 27.0004 26C27.0004 24.1435 26.2629 22.363 24.9501 21.0502C23.6374 19.7375 21.8569 19 20.0004 19C18.1439 19 16.3634 19.7375 15.0507 21.0502C13.7379 22.363 13.0004 24.1435 13.0004 26C13.0071 27.6725 13.618 29.2862 14.7204 30.544C13.569 31.2578 12.5746 32.1981 11.7976 33.3079C11.0206 34.4177 10.4772 35.6738 10.2004 37H8.0004V16.342L20.0004 5.67599L32.0004 16.342V37Z"
                fill="#878b9d"
              />
            </svg>
            <div class="applicant-card-details">
              <div class="applicant-card-title-wrapper">
                <h4 v-if="getApartments(idx).length === 1" class="title-strong">
                  Lägenhet {{ letters[idx] }}
                </h4>
                <h4 v-else class="title-strong">
                  Lägenhet {{ letters[idx] }}1 och {{ letters[idx] }}2
                </h4>
                <h5 v-if="isOwnApartment(idx)" class="your-apartment-title">
                  Din bostad
                </h5>
                <svg
                  v-if="!getApplicant(idx).owner"
                  width="28"
                  height="28"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M12 13C11.6955 12.9964 11.3973 13.0862 11.1454 13.2573C10.8936 13.4284 10.7001 13.6725 10.5912 13.9568C10.4823 14.2411 10.4631 14.552 10.5361 14.8476C10.6092 15.1431 10.7711 15.4092 11 15.61V17C11 17.2652 11.1054 17.5196 11.2929 17.7071C11.4804 17.8946 11.7348 18 12 18C12.2652 18 12.5196 17.8946 12.7071 17.7071C12.8946 17.5196 13 17.2652 13 17V15.61C13.2289 15.4092 13.3908 15.1431 13.4639 14.8476C13.5369 14.552 13.5177 14.2411 13.4088 13.9568C13.2999 13.6725 13.1064 13.4284 12.8546 13.2573C12.6027 13.0862 12.3045 12.9964 12 13V13ZM17 9V7C17 5.67392 16.4732 4.40215 15.5355 3.46447C14.5979 2.52678 13.3261 2 12 2C10.6739 2 9.40215 2.52678 8.46447 3.46447C7.52678 4.40215 7 5.67392 7 7V9C6.20435 9 5.44129 9.31607 4.87868 9.87868C4.31607 10.4413 4 11.2044 4 12V19C4 19.7956 4.31607 20.5587 4.87868 21.1213C5.44129 21.6839 6.20435 22 7 22H17C17.7956 22 18.5587 21.6839 19.1213 21.1213C19.6839 20.5587 20 19.7956 20 19V12C20 11.2044 19.6839 10.4413 19.1213 9.87868C18.5587 9.31607 17.7956 9 17 9ZM9 7C9 6.20435 9.31607 5.44129 9.87868 4.87868C10.4413 4.31607 11.2044 4 12 4C12.7956 4 13.5587 4.31607 14.1213 4.87868C14.6839 5.44129 15 6.20435 15 7V9H9V7ZM18 19C18 19.2652 17.8946 19.5196 17.7071 19.7071C17.5196 19.8946 17.2652 20 17 20H7C6.73478 20 6.48043 19.8946 6.29289 19.7071C6.10536 19.5196 6 19.2652 6 19V12C6 11.7348 6.10536 11.4804 6.29289 11.2929C6.48043 11.1054 6.73478 11 7 11H17C17.2652 11 17.5196 11.1054 17.7071 11.2929C17.8946 11.4804 18 11.7348 18 12V19Z"
                    fill="#878b9d"
                  />
                </svg>
              </div>

              <span class="description-text">{{ getDescription(idx) }}</span>
            </div>
          </div>
          <div
            v-for="(apartment, i) in getApartments(idx)"
            :key="i"
            class="applicant-card-body"
            :class="{
              'is-blurry':
                isRemovingApplicant(idx) || isTogglingApartmentCount(idx)
            }"
          >
            <div class="input-group">
              <div class="input-description-wrapper">
                <div class="label-wrapper">
                  <strong>För- och efternamn</strong>
                  <span
                    v-if="getApplicant(idx).owner && !isLb"
                    class="description-text"
                  >
                    / Skriv in för- och efternamn på personen som bor i Lägenhet
                    {{ letters[idx]
                    }}{{ getApartments(idx).length > 1 ? `${i + 1}` : '' }} i
                    nuläget.
                  </span>
                </div>
                <BaseIconText
                  v-if="!getApplicant(idx).owner"
                  :icon-url="svgs.ICONS.CHECK_WHITE_CIRCLE"
                  class="applicant-user-connected"
                  >Användare ansluten
                </BaseIconText>
              </div>
              <SwapInput
                v-model="getMembers(idx)[i].name"
                :disabled="isUpdating || !getApplicant(idx).owner"
                label=""
                description=""
                @input="updateMembersDebounced(idx)"
              />
            </div>

            <!-- Only enter email on other users -->
            <div
              v-if="
                i === 0 &&
                idx !== 0 &&
                getApplicant(idx).owner &&
                isApplicationOwner
              "
              class="input-group"
            >
              <div class="input-description-wrapper">
                <div class="label-wrapper">
                  <strong>E-post</strong>
                  <span class="description-text">
                    / Ange e-post om du vill bjuda in personen att hantera sina
                    egna uppgifter
                  </span>
                </div>
              </div>
              <div class="input-email">
                <SwapInput
                  ref="emailInputs"
                  v-model="getMembers(idx)[i].email"
                  :disabled="isUpdating || !getApplicant(idx).owner"
                  label=""
                  description=""
                  :validation="validEmail"
                  @input="updateMembersDebounced(idx)"
                />

                <InviteButton
                  :is-disabled="inviteButtonDisabled(idx, i)"
                  :applicant-id="getApplicant(idx).id"
                  :email="getMembers(idx)[i].email"
                  :all-emails="allEmails"
                  :invitation-sent="getApplicant(idx).invitationSent"
                  :invite-accepted="getApplicant(idx).inviteAccepted"
                  @connect="
                    connectApplicantIndex = idx;
                    connectAccountModalVisible = true;
                  "
                />
              </div>
            </div>
          </div>
        </div>

        <div v-if="!isLb" class="flex-center">
          <BaseButton
            v-if="!applicationIsSent && getApplicant(idx).owner"
            :is-transparent="true"
            :is-loading="false"
            :is-secondary="true"
            class="toggle-apartment-count"
            :disabled="isUpdating || applicationIsSent || applicationIsArchived"
            @click="toggleApartmentCount(idx)"
          >
            <BaseIconText
              v-if="getApartments(idx).length === 1"
              :icon-url="svgs.ICONS.ADD_CIRCLE_BLUE"
            >
              <span class="toggle-apartment-count-text">Har 2 lägenheter</span>
            </BaseIconText>
            <BaseIconText v-else :icon-url="svgs.ICONS.REMOVE_CIRCLE_BLUE"
              ><span class="toggle-apartment-count-text"
                >Ta bort andra lägenheten</span
              >
            </BaseIconText>
          </BaseButton>
        </div>
        <InfoButtonModal
          v-if="
            !isLb &&
            !applicationIsSent &&
            getApplicant(idx).owner &&
            getApartments(idx).length === 1
          "
          :disabled="isUpdating || applicationIsSent || applicationIsArchived"
          title="Har 2 lägenheter?"
          ><p>
            Klicka här om den här bytesparten har två lägenheter som ingår i
            byteskedjan. Ange då namn som representerar båda lägenheterna.
          </p>
        </InfoButtonModal>
        <div
          v-if="idx < form.applicants.length - 1"
          class="bottom-swap-arrow-wrapper"
        >
          <div class="arrow-body"></div>
          <div class="arrow-down-tip"></div>
        </div>
        <div v-if="!applicationIsSent" class="remove-applicant-wrapper">
          <BaseButton
            v-if="
              idx !== 0 &&
              form.applicants.length > 2 &&
              getApplicant(idx).owner &&
              isApplicationOwner
            "
            :is-disabled="isUpdating"
            :is-transparent="true"
            class="trashcan"
            @click="() => removeApplicant(idx)"
          >
            <div class="remove-applicant-circle">
              <svg
                width="18"
                height="20"
                viewBox="0 0 18 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M7 16C7.26522 16 7.51957 15.8946 7.70711 15.7071C7.89464 15.5196 8 15.2652 8 15V9C8 8.73478 7.89464 8.48043 7.70711 8.29289C7.51957 8.10536 7.26522 8 7 8C6.73478 8 6.48043 8.10536 6.29289 8.29289C6.10536 8.48043 6 8.73478 6 9V15C6 15.2652 6.10536 15.5196 6.29289 15.7071C6.48043 15.8946 6.73478 16 7 16ZM17 4H13V3C13 2.20435 12.6839 1.44129 12.1213 0.87868C11.5587 0.316071 10.7956 0 10 0H8C7.20435 0 6.44129 0.316071 5.87868 0.87868C5.31607 1.44129 5 2.20435 5 3V4H1C0.734784 4 0.48043 4.10536 0.292893 4.29289C0.105357 4.48043 0 4.73478 0 5C0 5.26522 0.105357 5.51957 0.292893 5.70711C0.48043 5.89464 0.734784 6 1 6H2V17C2 17.7956 2.31607 18.5587 2.87868 19.1213C3.44129 19.6839 4.20435 20 5 20H13C13.7956 20 14.5587 19.6839 15.1213 19.1213C15.6839 18.5587 16 17.7956 16 17V6H17C17.2652 6 17.5196 5.89464 17.7071 5.70711C17.8946 5.51957 18 5.26522 18 5C18 4.73478 17.8946 4.48043 17.7071 4.29289C17.5196 4.10536 17.2652 4 17 4ZM7 3C7 2.73478 7.10536 2.48043 7.29289 2.29289C7.48043 2.10536 7.73478 2 8 2H10C10.2652 2 10.5196 2.10536 10.7071 2.29289C10.8946 2.48043 11 2.73478 11 3V4H7V3ZM14 17C14 17.2652 13.8946 17.5196 13.7071 17.7071C13.5196 17.8946 13.2652 18 13 18H5C4.73478 18 4.48043 17.8946 4.29289 17.7071C4.10536 17.5196 4 17.2652 4 17V6H14V17ZM11 16C11.2652 16 11.5196 15.8946 11.7071 15.7071C11.8946 15.5196 12 15.2652 12 15V9C12 8.73478 11.8946 8.48043 11.7071 8.29289C11.5196 8.10536 11.2652 8 11 8C10.7348 8 10.4804 8.10536 10.2929 8.29289C10.1054 8.48043 10 8.73478 10 9V15C10 15.2652 10.1054 15.5196 10.2929 15.7071C10.4804 15.8946 10.7348 16 11 16Z"
                  fill="#409FFF"
                />
              </svg>
            </div>
          </BaseButton>
        </div>
      </div>

      <!-- Begin fake card -->
      <FakeApplicationCard
        v-if="isAddingApplicant"
        :key="form.applicants.length"
        :apartment-letter="letters[form.applicants.length]"
        :is-lb="isLb"
      />

      <div v-if="!isLb" class="add-apartment-wrapper">
        <BaseButton
          v-if="!applicationIsSent && isApplicationOwner"
          class="add-exchange-btn"
          :is-disabled="
            isUpdating || applicationIsSent || applicationIsArchived
          "
          :is-loading="false"
          :is-rounded="true"
          :is-secondary="true"
          @click="addApplicant"
        >
          <BaseIconText :icon-url="svgs.ICONS.ADD_CIRCLE">
            Lägg till bytespart
          </BaseIconText>
        </BaseButton>
      </div>

      <template v-if="!applicationIsSent">
        <BaseButton
          v-if="!applicant.started"
          :is-loading="isLoadingStartApplicant"
          :is-rounded="true"
          type="button"
          class="submit-btn"
          :is-fullwidth="isMobile"
          :disabled="
            isLoadingStartApplicant ||
            applicationIsSent ||
            applicationIsArchived
          "
          @click="handleStartApplicantClick"
        >
          <p>Starta Bytesansökan</p>
          <BaseIconText :icon-url="svgs.ICONS.ARROW_RIGHT" :align-right="true">
          </BaseIconText>
        </BaseButton>
        <BaseButton
          v-else
          :is-rounded="true"
          :is-fullwidth="isMobile"
          type="button"
          class="submit-btn"
          :disabled="isUpdating"
          @click="goToForm"
        >
          <p>Gå vidare till formulären</p>
          <BaseIconText :icon-url="svgs.ICONS.ARROW_RIGHT" :align-right="true">
          </BaseIconText>
        </BaseButton>
      </template>
    </section>
    <InfoModal
      v-if="errorModalVisible"
      title="Något gick fel"
      abort-label="Stäng"
      @click="handleErrorModalClick"
    >
      <p>Det gick inte att spara dina ändringar, försök igen senare.</p>
    </InfoModal>
    <DuplicatesModal
      v-if="duplicatesModalVisible"
      :applications="similarApplications"
      @close="closeDuplicatesModal"
      @selectApplication="selectApplication"
    />
    <ConnectAccountModal
      v-if="connectAccountModalVisible"
      :applicant="getApplicant(connectApplicantIndex)"
      @accepted="connectAccount()"
    />
  </div>
</template>
<script>
import debounce from '@/utils/debounce';
import isEqual from 'lodash.isequal';
import { mapMutations, mapGetters, mapState, mapActions } from 'vuex';
import SwapInput from '@/components/SwapInput.vue';
import svgs from '@/utils/icon-constants';
import BaseIconText from '@/components/BaseIconText.vue';
import BaseButton from '@/components/BaseButton.vue';
import ConnectAccountModal from '@/components/ConnectAccountModal.vue';
import InfoButtonModal from '@/components/InfoButtonModal.vue';
import InfoModal from '@/components/InfoModal.vue';
import CollapseCard from '@/components/CollapseCard.vue';
import ApplicationReviewedCard from '@/components/ApplicationReviewedCard.vue';
import { alphabet } from '@/utils/common';
import { validEmail } from '@/utils/input-validations';
import InviteButton from '@/components/InviteButton.vue';
import BaseLoadingSpinner from '@/components/BaseLoadingSpinner.vue';
import FakeApplicationCard from '@/components/FakeApplicationCard.vue';
import DuplicatesModal from '@/components/DuplicatesModal.vue';

export default {
  name: 'Application',
  components: {
    SwapInput,
    BaseButton,
    BaseIconText,
    InfoButtonModal,
    CollapseCard,
    InviteButton,
    InfoModal,
    ConnectAccountModal,
    BaseLoadingSpinner,
    ApplicationReviewedCard,
    FakeApplicationCard,
    DuplicatesModal
  },
  beforeRouteLeave(to, from, next) {
    this.isUpdatingApplication(false);
    next();
  },
  data() {
    return {
      form: null,
      isUpdating: false,
      isAddingApplicant: false,
      isRemovingIdx: -1,
      isTogglingApartmentsIdx: -1,
      letters: alphabet,
      svgs,
      connectAccountModalVisible: false,
      connectApplicantIndex: null,
      isLoadingStartApplicant: false,
      errorModalVisible: false,
      validEmail,
      duplicatesModalVisible: false,
      similarApplications: []
    };
  },
  computed: {
    ...mapGetters({
      applicationIsSent: 'application/applicationIsSent',
      applicationIsArchived: 'application/applicationIsArchived',
      isApplicationOwner: 'application/isApplicationOwner',
      isLb: 'application/isLb',
      notStarted: 'application/notStarted',
      applicant: 'application/applicant',
      isMobile: 'app/isMobile'
    }),
    ...mapState({
      application: state => state.application.application,
      applicationId: state => state.application.applicationId
    }),

    currentUserIndex() {
      return 0;
    },
    canEdit() {
      return this.form.applicants.filter(a => a.email !== null);
    },
    allEmails() {
      if (!this.form) {
        return [];
      }
      return [
        ...new Set([
          ...this.form.applicants.map(applicant => applicant.email),
          ...this.form.applicants.map(applicant => applicant.members[0].email)
        ])
      ].filter(email => !!email && email.length > 0);
    }
  },
  watch: {
    application(newVal) {
      if (!newVal) {
        return;
      }

      this.form = JSON.parse(JSON.stringify(newVal));
    },
    allEmails(newVal, oldVal) {
      if (oldVal.length && !isEqual(newVal, oldVal)) {
        if (newVal.every(email => this.validEmail(email))) {
          this.checkDuplicatesDebounced(
            newVal.map(email => encodeURIComponent(email))
          );
        }
      }
    }
  },
  created() {
    this.updateMembersDebounced = debounce(this.updateMembers, 400);
    this.checkDuplicatesDebounced = debounce(this.checkDuplicates, 1000);
    if (this.application) {
      this.syncFormData();
    }
  },
  methods: {
    ...mapActions({
      getNextPath: 'application/getNextPath',
      startApplicant: 'application/startApplicant'
    }),
    ...mapMutations({
      setApplicationId: 'application/setApplicationId',
      setAppReady: 'app/setAppReady',
      setApplication: 'application/setApplication',
      setSelectedApplicantIndex: 'application/setSelectedApplicantIndex',
      toggleSideMenuDisabled: 'app/toggleSideMenuDisabled'
    }),
    async selectApplication(id) {
      this.duplicatesModalVisible = false;
      const currentApplicationId = this.applicationId;
      const selectedId = id;
      this.$applicationService.removeApplication(currentApplicationId);

      //Wait for modal to transition
      setTimeout(async () => {
        this.setApplicationId(selectedId);
        this.setAppReady(false);
        this.$emit('initiate');
      }, 200);
    },
    inviteButtonDisabled(idx, memberIdx) {
      return (
        this.applicationIsSent ||
        this.applicationIsArchived ||
        !this.validEmail(this.getMembers(idx)[memberIdx].email ?? '')
      );
    },
    isUpdatingApplication(isUpdating) {
      this.isUpdating = isUpdating;
      this.toggleSideMenuDisabled(isUpdating);
    },
    syncFormData() {
      this.form = JSON.parse(JSON.stringify(this.application));
    },
    handleErrorModalClick() {
      this.errorModalVisible = false;
    },
    async handleStartApplicantClick() {
      this.isLoadingStartApplicant = true;
      await this.startApplicant(this.applicant.id);
      this.isLoadingStartApplicant = false;
    },

    async goToForm() {
      const nextPath = await this.getNextPath(this.$router);
      await this.$router.push({
        path: nextPath
      });
    },

    getApplicant(applicantIndex) {
      return this.form.applicants[applicantIndex];
    },

    getMembers(applicantIndex) {
      return this.getApplicant(applicantIndex).members;
    },

    getApartments(applicantIndex) {
      return this.getApplicant(applicantIndex).apartments;
    },

    isOwnApartment(applicantIndex) {
      return applicantIndex === this.currentUserIndex;
    },

    getDescription(applicantIndex) {
      let description = '';

      if (
        applicantIndex ===
        (this.currentUserIndex + 1) % this.form.applicants.length
      ) {
        const memberCount = this.getMembers(0).length;
        return `Dit ${memberCount === 1 ? 'du' : 'Ni'} ska flytta.`;
      }

      const previousIndex = this.getPreviousApplicantIndex(applicantIndex);
      const previousApplicantLetter = this.letters[previousIndex];
      const apartmentCount = this.getApartments(previousIndex).length;
      if (apartmentCount === 1) {
        description += `Dit lägenhet ${previousApplicantLetter} ska flytta.`;
      } else {
        description += `Dit lägenhet ${previousApplicantLetter}1 och ${previousApplicantLetter}2 ska flytta.`;
      }

      return description;
    },

    getPreviousApplicantIndex(applicantIndex) {
      if (applicantIndex === 0) {
        return this.form.applicants.length - 1;
      }

      return applicantIndex - 1;
    },

    async addApplicant() {
      this.isUpdatingApplication(true);
      this.isAddingApplicant = true;

      this.$nextTick(() =>
        window.scrollTo({
          top: document.body.scrollHeight,
          behavior: 'smooth'
        })
      );

      try {
        this.form = await this.$applicationService.createNewApplicant(
          this.form.id
        );
        this.setApplication(JSON.parse(JSON.stringify(this.form)));
      } catch {
        this.errorModalVisible = true;
        this.syncFormData();
      }

      this.isUpdatingApplication(false);
      this.isAddingApplicant = false;
    },

    async removeApplicant(applicantIndex) {
      this.isUpdatingApplication(true);
      this.isRemovingIdx = applicantIndex;
      const applicant = this.getApplicant(applicantIndex);

      try {
        await this.$applicationService.removeApplicant(
          this.form.id,
          applicant.id
        );
        this.setSelectedApplicantIndex(0);
        this.form.applicants.splice(applicantIndex, 1);
        this.setApplication(JSON.parse(JSON.stringify(this.form)));
      } catch {
        this.errorModalVisible = true;
        this.syncFormData();
      }

      this.isUpdatingApplication(false);
      this.isRemovingIdx = -1;
    },

    async toggleApartmentCount(applicantIndex) {
      this.isUpdatingApplication(true);
      this.isTogglingApartmentsIdx = applicantIndex;
      const applicant = this.getApplicant(applicantIndex);
      const { id, apartments } = applicant;

      try {
        if (apartments.length === 1) {
          const member1Name = applicant.members[0].name;
          this.form = await this.$applicationService.addSecondApartment(id);

          // If name is changed during update
          if (
            this.getApplicant(applicantIndex).members[0].name !== member1Name
          ) {
            this.getApplicant(applicantIndex).members[0].name = member1Name;
            this.updateMembersDebounced(applicantIndex);
          }
        } else {
          this.form = await this.$applicationService.removeSecondApartment(id);
        }
        this.setApplication(JSON.parse(JSON.stringify(this.form)));
      } catch {
        this.errorModalVisible = true;
        this.syncFormData();
      }
      this.isUpdatingApplication(false);
      this.isTogglingApartmentsIdx = -1;
    },

    async updateMembers(applicantIndex) {
      const applicant = this.getApplicant(applicantIndex);
      const res = await this.$applicationService.updateMembers(
        applicant.id,
        applicant.members
      );

      if (res?.status === 200) {
        this.setApplication(JSON.parse(JSON.stringify(this.form)));
      }
    },

    async checkDuplicates(encodedEmails) {
      this.similarApplications = [];
      this.duplicatesModalVisible = false;
      try {
        const result =
          await this.$applicationService.checkDuplicates(encodedEmails);
        if (Object.keys(result).length > 0) {
          // Unfocus input field when presenting modal
          this.$refs.emailInputs.forEach(emailInput => {
            emailInput.$el.querySelector('input').blur();
          });
          this.duplicatesModalVisible = true;
          this.similarApplications = result;
        }
      } catch {
        console.error('Error when checking duplicates');
      }
    },

    async connectAccount() {
      this.syncFormData();
      this.connectAccountModalVisible = false;
    },
    isRemovingApplicant(idx) {
      return idx === this.isRemovingIdx;
    },
    isTogglingApartmentCount(idx) {
      return idx === this.isTogglingApartmentsIdx;
    },
    closeDuplicatesModal() {
      this.duplicatesModalVisible = false;
      this.similarApplications = [];
    }
  }
};
</script>
<style lang="scss">
$arrow-size: 15px;
$arrow-size-mobile: 12px;

.application-editor {
  padding: 0px 5rem;
  @include mobile {
    padding: 0 2.2rem;
    margin-bottom: 5rem;
  }
}

.page-title {
  font-size: 3rem;
  @include mobile {
    font-size: 2.4rem;
  }
}

.applicant-card {
  margin-top: 25px;
  background: #fff;
  border-radius: 10px;
  border: 1px solid #e8e8e8;
  position: relative;
  &.is-locked {
    opacity: 0.7;
  }
}
.is-blurry {
  filter: blur(3px);
  opacity: 0.5;
}

.applicant-spinner {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 2;
}

.applicant-card .is-saving {
  background: red !important;
}

.application-wrapper {
  position: relative;
}

.add-apartment-wrapper {
  margin-top: 30px;
  text-align: right;
  @include mobile {
    margin-top: 0;
  }
}

.add-exchange-btn {
  @include mobile {
    width: 100%;
  }
}

.card-info {
  margin-left: 20px;
  display: flex;
  justify-content: center;

  @include mobile {
    margin-left: 10px;
  }
}

.applicant-card-head {
  display: flex;
  flex-direction: row;
  border-bottom: 1px solid var(--color-light-gray);
  @include mobile {
    .icon {
      display: none;
    }
  }
}

.applicant-card-head img {
  margin-right: 5px;
}

.title-strong {
  font-size: 1.8rem;
  font-weight: 600;
  font-family:
    Averta Std,
    Helvetica,
    Arial,
    sans-serif;
}

.your-apartment-title {
  font-size: 1.6rem;
  font-family:
    Averta Std,
    Helvetica,
    Arial,
    sans-serif;
}

.applicant-card-details {
  margin-left: 20px;
  flex: 1 1 auto;
  @include mobile {
    margin: 0;
  }
  .title-strong {
    flex: 1 1 auto;
  }
}

.applicant-card-title-wrapper {
  display: flex;
}

.description-text {
  font-size: 1.4rem;
  color: var(--color-gray);
}

.applicant-card-body,
.applicant-card-head {
  padding: 14px 24px;
  @include mobile {
    padding: 14px 10px;
  }
}

.applicant-card-body .field-wrapper {
  margin-bottom: 6px;
}

.updating input {
  pointer-events: none;
}

.toggle-apartment-count {
  float: left;
  padding: 0 !important;
}

.toggle-apartment-count-text {
  font-family: 'Poppins';
  font-style: normal;
  font-weight: 500;
  font-size: 1.4rem;
  color: var(--color-blue);
}

.toggle-apartment-count img {
  width: 20px;
  height: 20px;
  margin: 5px;
}

.side-swap-arrow-wrapper {
  left: -50px;
  top: -25px;
  bottom: -25px;
  width: 10px;
  background: var(--color-gray);
  position: absolute;
  @include mobile {
    left: -33px;
    width: 8px;
  }
}

.side-swap-arrow-wrapper.first {
  top: auto;
  bottom: 0px;
  width: 10px;
  height: 50%;
  @include mobile {
    width: 8px;
  }
}

.side-swap-arrow-wrapper.last {
  left: -50px;
  top: -100px;
  width: 10px;
  height: 200px;
  background: var(--color-gray);
  position: absolute;
  @include mobile {
    left: -33px;
    width: 8px;
  }
}

.side-swap-arrow-wrapper .arrow-body {
  width: 35px;
  height: 10px;
  top: 0px;
  left: 0px;
  background: var(--color-gray);
  position: absolute;
  @include mobile {
    width: 12px;
    left: 7px;
    height: 8px;
  }
}

.side-swap-arrow-wrapper.last .arrow-body {
  top: auto;
  bottom: 0px;
  width: 22px;
  left: 7px;
  height: 10px;
  @include mobile {
    height: 8px;
  }
}

.side-swap-arrow-wrapper .arrow-right-tip {
  width: 0;
  height: 0;
  border-top: $arrow-size solid transparent;
  border-bottom: $arrow-size solid transparent;
  border-left: $arrow-size solid var(--color-gray);
  top: -10px;
  left: 25px;
  position: absolute;
  @include mobile {
    left: 18px;
    top: -8px;
    border-top: $arrow-size-mobile solid transparent;
    border-bottom: $arrow-size-mobile solid transparent;
    border-left: $arrow-size-mobile solid var(--color-gray);
  }
}

.bottom-swap-arrow-wrapper {
  position: relative;
  margin-left: 50%;
  margin-top: 0;
  margin-bottom: 20px;
  height: 25px;
}

.bottom-swap-arrow-wrapper .arrow-body {
  width: 10px;
  height: 20px;
  background: var(--color-gray);
}

.bottom-swap-arrow-wrapper .arrow-down-tip {
  display: block;
  width: 0;
  height: 0;
  border-left: $arrow-size solid transparent;
  border-right: $arrow-size solid transparent;
  border-top: $arrow-size solid var(--color-gray);
  top: 17px;
  left: -10px;
  position: absolute;
}

.remove-applicant-wrapper {
  position: absolute;
  right: -85px;
  top: calc(50% - 50px);
  @include mobile {
    top: 0;
    right: 0;
    transform: translateY(-100%);
    .trashcan {
      padding: 15px 5px 15px 15px;
    }
  }
}

.remove-applicant-circle {
  transition: background 0.4s ease;
  border-radius: 100%;
  padding: 10px;
}

.remove-applicant-circle:hover {
  background: #dcecfd;
}

.saving-message {
  flex-grow: 1;
  text-align: right;
  color: #34c375;
}

.remove-applicant-circle:hover svg path {
  transition: fill 0.4s ease;
  fill: #ff3843;
}

textarea[disabled='disabled'],
input[disabled='disabled'] {
  color: var(--color-disabled);
}

.flex-center {
  display: inline-flex;
  align-items: center;
}

.input-email {
  display: flex;
  justify-content: space-between;

  @include mobile {
    display: block;
  }
}

.input-group {
  &:nth-child(1) {
    margin-top: 1rem;
  }
  &:not(:nth-child(1)) {
    margin-top: 2rem;
  }
  strong {
    font-weight: 600;
  }
}

.input-description-wrapper {
  margin-bottom: 1rem;
  display: flex;
  justify-content: space-between;

  .applicant-user-connected {
    font-size: 1.4rem;
    color: var(--color-gray);
    img {
      margin-top: 2px;
      max-height: 14px;
      margin-right: 4px;
      filter: invert(57%) sepia(98%) saturate(333%) hue-rotate(94deg)
        brightness(94%) contrast(90%);
    }
  }
}

.submit-btn {
  @include mobile {
    margin-top: 2rem;
  }
}
</style>
